// SearchResultsPage.js
import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    Container,
    Row,
    Col,
    // BootstrapCard, // No longer directly used by IntegratedSearchResultContestItem
    ListGroup,
    Spinner,
    Alert,
    Image,
    Badge
} from 'react-bootstrap';
import moment from 'moment';
import CardList from './CardList'; // Your existing CardList for displaying cards
import { useUser } from '../UserContext'; // For settings, if needed for CardList
import { FaUser, FaExternalLinkAlt } from 'react-icons/fa';

// Main CSS for the page (and now for the integrated contest item)
import './SearchResultsPage.css'; // Make sure this CSS file will contain the new styles

// ======== INTEGRATED SearchResultContestItem Component (Modified) ========
const IntegratedSearchResultContestItem = ({ contest }) => {
    const {
        id,
        title,
        description,
        short,
        image_url,
        start_date,
        submission_count
    } = contest;

    return (
        // Applied 'past-contest-card' class for hover effect styling
        // The Col sizing (xs, sm, md) is kept from original SearchResultsPage for now.
        // If you want it to match ContestList's past contest grid (xs={2} md={4} lg={5}),
        // you'd change these Col props AND potentially the <Row> props in the parent.
        // For this example, I'm focusing on the *individual item's appearance*.
        <Col xs={6} sm={4} md={2} className="mb-4 text-center past-contest-card">
            <Link to={`/contests/${id}/`} style={{ textDecoration: 'none', color: 'inherit' }}>
                <div className="ratio ratio-1x1 mb-2">
                    <img
                        src={
                            image_url ||
                            `https://www.hearthcards.net/images/contest/sketch/${id}.svg`
                        }
                        alt={title}
                        className="img-fluid" // Standard img, BootstrapCard.Img removed
                        style={{ objectFit: 'cover' }} // To match ContestList style
                    />
                </div>
                <div style={{ fontSize: "1rem", wordWrap: 'break-word' }} className="mb-1 contest-title-search" title={title}>
                    {/* Using a class for easier targeting if needed, kept original truncation logic */}
                    {title.length > 50 ? title.substring(0, 47) + '...' : title}
                </div>
                <div
                    className="text-muted small d-flex justify-content-center align-items-center flex-wrap"
                    style={{ gap: '0.5rem', lineHeight: '1.2' }}
                >
                    <span className="d-flex align-items-center" title={`${submission_count} submissions`}>
                        <FaUser className="me-1" />
                        {submission_count}
                    </span>
                    {/* You can add a <span className="vr"></span> here if you want the vertical separator */}
                    {start_date && (
                        <span className="d-flex align-items-center">
                            {moment.unix(start_date).format("MMM D, YYYY")}
                        </span>
                    )}
                </div>
            </Link>
            {/* Description overlay, using 'description' field from search results */}
            {short && (
                <div className="contest-description-overlay">
                    <div
                        className="description-content"
                        dangerouslySetInnerHTML={{ __html: short }}
                    />
                </div>
            )}
        </Col>
    );
};
// ======== END INTEGRATED SearchResultContestItem Component ========


const SearchResultsPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const query = searchParams.get('q');
    const [results, setResults] = useState({ cards: [], users: [], contests: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { settings } = useUser();

    useEffect(() => {
        if (query && query.trim() !== "") {
            setLoading(true);
            setError(null);
            axios.get(`/api/search_full.php?q=${encodeURIComponent(query)}`)
                .then(response => {
                    setResults(response.data);
                    setLoading(false);
                })
                .catch(err => {
                    console.error('Error fetching full search results:', err);
                    setError('Failed to load search results. Please try again.');
                    setResults({ cards: [], users: [], contests: [] });
                    setLoading(false);
                });
        } else {
            setLoading(false);
            setResults({ cards: [], users: [], contests: [] });
        }
    }, [query]);

    const handleUserClick = (userId) => {
        navigate(`/user/${userId}/cards`);
    };

    if (loading) {
        return (
            <Container className="text-center mt-5">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
                <p className="mt-2">Loading results for "{query}"...</p>
            </Container>
        );
    }

    if (error) {
        return <Container><Alert variant="danger" className="mt-3">{error}</Alert></Container>;
    }

    const noResultsFound = results.cards.length === 0 && results.users.length === 0 && results.contests.length === 0;

    return (
        <Container className="mt-4 search-results-page">
            <Row>
                <Col>
                    <h2 className="mb-3">Search Results for "{query}"</h2>
                </Col>
            </Row>

            {noResultsFound && !loading && (
                <Alert variant="info" className="mt-3">
                    No results found for "{query}". Try a different search term.
                </Alert>
            )}

            {/* Cards Section */}
            {results.cards && results.cards.length > 0 && (
                <section className="search-results-section mb-5">
                    <h3 className="section-title mb-5">
                        Cards <Badge bg="secondary">{results.cards.length}</Badge>
                    </h3>
                    {/* <hr /> */}
                    <CardList cards={results.cards} loading={false} settings={settings} />
                </section>
            )}

            {/* Users Section */}
            {results.users && results.users.length > 0 && (
                <section className="search-results-section mb-5 mx-2 mx-sm-5">
                    <h3 className="section-title mb-5">
                        Users <Badge bg="secondary">{results.users.length}</Badge>
                    </h3>
                    {/* <hr /> */}
                    <ListGroup variant="flush">
                        {results.users.map(user => (
                            <ListGroup.Item
                                key={`user-${user.id}`}
                                action
                                onClick={() => handleUserClick(user.id)}
                                className="d-flex align-items-center search-result-user-item"
                            >
                                <Image
                                    src={
                                        user.profile_picture
                                            ? (user.profile_picture.includes('http')
                                                ? user.profile_picture
                                                : `/images/users/pictures/${user.profile_picture}`)
                                            : "/gallery/images/profile-image.jpg"
                                    }
                                    roundedCircle
                                    className="me-3 search-result-user-avatar"
                                    alt={user.username}
                                />
                                <div className="flex-grow-1">
                                    <span className="fw-bold">{user.username}</span>
                                </div>
                                <FaExternalLinkAlt className="ms-2 text-muted" />
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                </section>
            )}

            {/* Contests Section */}
            {results.contests && results.contests.length > 0 && (
                <section className="search-results-section mb-5 mx-2 mx-sm-5">
                    <h3 className="section-title mb-5">
                        Contests <Badge bg="secondary">{results.contests.length}</Badge>
                    </h3>
                    {/* <hr /> */}
                    {/* The Row's g-4 gives spacing. The Col props in IntegratedSearchResultContestItem define items per row. */}
                    {/* If you want same items per row as ContestList (xs={2} md={4} lg={5}), you'd adjust */}
                    {/* the props on the Row here e.g. <Row xs={2} md={4} lg={5} className="g-4"> */}
                    {/* AND the Col props within IntegratedSearchResultContestItem would need to be removed or be just <Col> */}
                    <Row className="g-4">
                        {results.contests.map(contest => (
                            <IntegratedSearchResultContestItem key={`contest-${contest.id}`} contest={contest} />
                        ))}
                    </Row>
                </section>
            )}
        </Container>
    );
};

export default SearchResultsPage;